import React, {useEffect, useState} from 'react'
import AdvancedChannelSettingsPage from './AdvancedChannelSettingsPage'
import {useAppDispatch, useAppSelector} from 'src/redux/storeHooks'
import {useLocation, useNavigate, useParams} from 'react-router-dom'
import {checkDynamicSettingPair} from 'src/utils/checkDynamicSettingPair'
import useAppToast from 'src/hooks/useAppToast'
import { getAllPlatformTypesAsync, fetchConnectedPlatformAsync } from 'src/redux/actions/platformActions'
import Loader from 'src/utils/Loader'
import PlatformAdditionalMappingGate from 'src/app/pages/listing/components/PlatformAdditionalMappingGate'
import {isSourceConnectedPlatform} from 'src/utils/platformConnectionValidation'

const AdvancedChannelSettingsWrapper: React.FC = () => {
  const [key, setKey] = useState<number>(0)
  const {
    mainStoreData,
    shippingStoreData,
    accountingStoreData,
    erpStoreData,
    omsStoreData,
    ticketStoreData,
    fetchedConnectedPlatformData,
    fetchedConnectedPlatformLoading,
  } = useAppSelector(
    (state) => state.fetchConnectedPlatform
  )
  const {warningToast} = useAppToast()
  const {selectedPlatformId} = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(true)
  const [platformsHydrated, setPlatformsHydrated] = useState(false)

  useEffect(() => {
    if (!selectedPlatformId) return

    setPlatformsHydrated(false)
    dispatch(fetchConnectedPlatformAsync()).finally(() => setPlatformsHydrated(true))
  }, [dispatch, selectedPlatformId])

  const isFirstConnectSession =
    !!selectedPlatformId &&
    Object.keys(localStorage).some(
      (key) =>
        key.startsWith('platformFirstConnect_') &&
        key.endsWith(`_${selectedPlatformId}`) &&
        localStorage.getItem(key) === 'true'
    )

  const linkedChannels = async (secondary_platform_id: string | undefined) => {
    const res = await dispatch(getAllPlatformTypesAsync({ platform_id: secondary_platform_id! }))
    const data = res?.payload?.data
    const selfData = res?.payload?.self_data

    return {
      mainCodes: Array.isArray(data) ? data.map((item: any) => item.name) : [],
      selectedCode: selfData?.code ?? selfData?.name,
    }
  }

  useEffect(() => {
    if (!platformsHydrated || fetchedConnectedPlatformLoading) return

    if (isFirstConnectSession) {
      setLoading(false)
      return
    }

    if (!mainStoreData && !shippingStoreData && !accountingStoreData && !erpStoreData && !omsStoreData) {
        warningToast(
          'Please connect a Source, Accounting, Shipping, ERP, or OMS channel to proceed destination channel settings.',
          {
            theme: 'colored',
            position: 'top-center',
            style: { width: '650px' },
          }
        )
        navigate('/platforms')
        return
      }
      const isBetaRoute = location.pathname.startsWith('/platform-setting-beta')
      if (isBetaRoute) {
        setLoading(false)
        return
      }

      async function checkAccess() {
        const { mainCodes, selectedCode } = await linkedChannels(selectedPlatformId)
        const hasValidSelection = !!selectedPlatformId

        const allowAccess = hasValidSelection && checkDynamicSettingPair(mainCodes, selectedCode)

        if (!allowAccess) {
          navigate('/listing-channels')
        }
        setLoading(false)
      }

      const selectedConnectedPlatform = fetchedConnectedPlatformData?.find(
        (platform) => platform.id === Number(selectedPlatformId)
      )
 // Safe Guard: Hide OMS if it is already connected
      if (selectedConnectedPlatform) {
        const isOmsPlatform = 
          selectedConnectedPlatform.isOmsStore === true || 
          selectedConnectedPlatform.type?.toUpperCase() === 'OMS' ||
          selectedConnectedPlatform.name?.toUpperCase() === 'COMMERCIUMOMS' ||
          selectedConnectedPlatform.display_name?.toUpperCase() === 'COMMERCIUM OMS'

        if (isOmsPlatform) {
          navigate('/listing-channels', { replace: true })
          return
        }
      }

      const isKnownSourcePlatform =
        selectedConnectedPlatform != null &&
        isSourceConnectedPlatform(selectedConnectedPlatform)

      if (
        isKnownSourcePlatform ||
        mainStoreData?.id === Number(selectedPlatformId) ||
        accountingStoreData?.id === Number(selectedPlatformId) ||
        shippingStoreData?.id === Number(selectedPlatformId) ||
        erpStoreData?.id === Number(selectedPlatformId) ||
        omsStoreData?.id === Number(selectedPlatformId) ||
        ticketStoreData?.id === Number(selectedPlatformId)
      ) {
        setLoading(false)
        return
      }

      checkAccess()
  }, [
    platformsHydrated,
    fetchedConnectedPlatformData,
    mainStoreData,
    accountingStoreData,
    shippingStoreData,
    erpStoreData,
    omsStoreData,
    ticketStoreData,
    fetchedConnectedPlatformLoading,
    location.pathname,
    selectedPlatformId,
    isFirstConnectSession,
  ])

  const handleSave = (): void => {
    setKey((prev) => prev + 1)
  }

  return loading ? (
    <Loader variant="content" />
  ) : (
    <PlatformAdditionalMappingGate selectedPlatformId={selectedPlatformId}>
      <AdvancedChannelSettingsPage key={key} onSave={handleSave} />
    </PlatformAdditionalMappingGate>
  )
}

export default AdvancedChannelSettingsWrapper

